// Which numbering series a shipment's tracking number comes from (03 §3.5, D-27): the branch
// default, the customer's series when the shipper has one, the agent's when an agent is chosen.
// Staff may pick another by hand. Pure: the office counter and the ops pickup share it.
import {
  branchDefaultSeries,
  normaliseTrackingInput,
  seriesExamples,
  trackingNoProblem,
} from '@ecobox/domain';
import type { NumberSeriesRecord } from '@ecobox/schema';

export interface NumberingOption {
  id: string;
  label: string;
  /** The next-but-one number in this series' shape, to recognise it by. */
  example: string;
  kind: 'branch' | 'customer' | 'agent';
}

/** The branch's default series: its document, or the 6.0 shape when none is saved yet. */
function branchDefault(all: readonly NumberSeriesRecord[], branchId: string): NumberSeriesRecord {
  const saved = all.find((s) => s.id === branchId && s.active !== false);
  return (
    saved ?? {
      ...branchDefaultSeries(branchId),
      name: `Default ${branchId}`,
      startNumber: 1,
      assignedTo: { kind: 'branch', branchId },
      active: true,
    }
  );
}

/** What can be picked at this branch: its default series, then every customer and agent series. */
export function numberingOptions(
  all: readonly NumberSeriesRecord[],
  branchId: string,
  year: number,
): NumberingOption[] {
  const others = all
    .filter((s) => s.active !== false && s.assignedTo.kind !== 'branch')
    .sort((a, b) => a.name.localeCompare(b.name));
  return [branchDefault(all, branchId), ...others].map((s) => ({
    id: s.id,
    label: s.assignedTo.kind === 'branch' ? `Branch default (${branchId})` : s.name,
    example: seriesExamples(s, s.startNumber, year)[0] ?? '',
    kind: s.assignedTo.kind,
  }));
}

/**
 * The series the shipment should use: the agent's when an agent with a series is chosen, else the
 * shipper's when that customer has a series, else the branch default (its id is the branch code).
 */
export function autoSeries(
  all: readonly NumberSeriesRecord[],
  branchId: string,
  who: { customerId?: string | null | undefined; agentId?: string | null | undefined },
): { id: string; why: string | undefined } {
  const active = all.filter((s) => s.active !== false);
  const agent = who.agentId
    ? active.find((s) => s.assignedTo.kind === 'agent' && s.assignedTo.agentId === who.agentId)
    : undefined;
  if (agent) return { id: agent.id, why: `The agent's own series: ${agent.name}.` };
  const customer = who.customerId
    ? active.find(
        (s) => s.assignedTo.kind === 'customer' && s.assignedTo.customerId === who.customerId,
      )
    : undefined;
  if (customer) return { id: customer.id, why: `This customer's own series: ${customer.name}.` };
  return { id: branchId, why: undefined };
}

/** A typed tracking number as stored, and why it cannot be one (null when it can). */
export function checkTypedTrackingNo(text: string): { value: string; problem: string | null } {
  const value = normaliseTrackingInput(text);
  return { value, problem: trackingNoProblem(value) };
}
